import type React from 'react';

import { Button } from '@vlabs/uikit/components/controls/button';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './ResetUserPasswordModal.module.sass';
import { useResetUserPasswordTranslation } from '../../i18n';
import type { ResetUserPassword } from '../../types';
import { useResetUserPasswordWithConfirmation } from '../../useResetUserPasswordWithConfirmation';

const displayName = 'ResetUserPasswordModal';
const testid = buildTestid(displayName);

interface ResetUserPasswordModalProps extends ResetUserPassword, WithDataTestid {
  onClose: VoidFunction;
}

function ResetUserPasswordModal({
  userId,
  onClose,
  'data-testid': dataTestId = undefined,
}: ResetUserPasswordModalProps): React.JSX.Element {
  const { t } = useResetUserPasswordTranslation();
  const resetUserPasswordWithConfirmation = useResetUserPasswordWithConfirmation({ userId });

  const resetPasswordByConfirmation = () => {
    if (!userId) return;
    onClose();
    resetUserPasswordWithConfirmation().catch(() => undefined);
  };

  return (
    <div
      className={st.ResetUserPasswordModal}
      {...testid(dataTestId)}
    >
      <div className={st.Description}>
        <h5 className={st.Title}>{t('modal_title')}</h5>
        <span className={st.Subtitle}>{t('modal_subtitle')}</span>
      </div>

      <div className={st.Actions}>
        <Button
          className={st.Action}
          disabled
        >
          {t('modal_email_button')}
        </Button>

        <Button
          className={st.Action}
          disabled={!userId}
          kind="secondary"
          onClick={resetPasswordByConfirmation}
          variant="filled"
        >
          {t('modal_confirmation_button')}
        </Button>

        <Button
          className={st.Action}
          kind="secondary"
          onClick={onClose}
          variant="outlined"
        >
          {t('modal_cancel_button')}
        </Button>
      </div>
    </div>
  );
}

ResetUserPasswordModal.displayName = displayName;

export { ResetUserPasswordModal };
